import { useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { Input, Select } from "../../components/ui/Input";
import { LabelWithHint } from "../../components/ui/Tooltip";
import { majorToMinor, minorToMajor } from "../../lib/money";
import { SellingPlanOptions } from "../products/SellingPlanOptions";
import type { GiftDiscountKind, GiftProductNode, GiftSubscriptionMode } from "../../lib/types";

const FLOOR_HELP =
	"Stop offering this once giving it away would push stock below this number. Set 50 to keep 50 units in reserve for paying customers. Leave at 0 to give it away until stock runs out.";

const DISCOUNT_HELP =
	"How much comes off this product. Free takes it to zero. Percentage and amount leave the shopper paying the rest.";

const VALUE_HELP =
	"Shown to the shopper as “a $20 value”. Display only, and they are never charged it.";

const SUBSCRIPTION_HELP =
	"Optional lets the shopper take it once or on a subscription. Required means it is only given on a subscription, so the one time choice is not offered.";

const PLAN_HELP =
	"Pin this to one plan, so it is given on a monthly subscription but not an annual one. Any plan lets the shopper choose from whatever the product is sold on.";

type Draft = {
	value: string;
	floor: string;
	discountKind: GiftDiscountKind;
	discountAmount: string;
	subscriptionMode: GiftSubscriptionMode;
	sellingPlanGid: string | null;
};

const toDraft = (node: GiftProductNode): Draft => ({
	value: minorToMajor(node.msrpMinor),
	floor: String(node.availabilityFloor),
	discountKind: node.discountKind,
	discountAmount:
		node.discountKind === "percentage"
			? String(node.discountValue / 100)
			: node.discountKind === "fixed_amount"
				? minorToMajor(node.discountValue)
				: "",
	subscriptionMode: node.subscriptionMode,
	sellingPlanGid: node.sellingPlanGid,
});

const discountValueFrom = (draft: Draft): number => {
	if (draft.discountKind === "free") return 0;
	if (draft.discountKind === "percentage") {
		return Math.round(Number(draft.discountAmount || "0") * 100);
	}
	return majorToMinor(draft.discountAmount || "0");
};

type ItemSettingsDialogProps = {
	node: GiftProductNode | null;
	saving: boolean;
	onClose: () => void;
	onSave: (entryId: string, settings: Record<string, unknown>) => void;
};

export const ItemSettingsDialog = ({ node, saving, onClose, onSave }: ItemSettingsDialogProps) => {
	const [draft, setDraft] = useState<Draft | null>(null);

	useEffect(() => {
		setDraft(node ? toDraft(node) : null);
	}, [node]);

	if (!node || !draft) return null;

	const submit = () =>
		onSave(node.id, {
			msrpMinor: majorToMinor(draft.value || "0"),
			availabilityFloor: Number(draft.floor || "0"),
			discountKind: draft.discountKind,
			discountValue: discountValueFrom(draft),
			subscriptionMode: draft.subscriptionMode,
			sellingPlanGid: draft.subscriptionMode === "none" ? null : draft.sellingPlanGid,
		});

	return (
		<Dialog
			open
			onOpenChange={(next) => !next && onClose()}
			title={node.product.title ?? "Product settings"}
			footer={
				<>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button onClick={submit} disabled={saving}>
						{saving ? "Saving…" : "Save"}
					</Button>
				</>
			}
		>
			<div className="space-y-4">
				<div className="grid gap-4 sm:grid-cols-2">
					<label className="block space-y-1.5">
						<LabelWithHint label="Value you advertise">{VALUE_HELP}</LabelWithHint>
						<Input
							type="number"
							min="0"
							step="0.01"
							placeholder="20.00"
							value={draft.value}
							onChange={(event) => setDraft({ ...draft, value: event.target.value })}
						/>
					</label>
					<label className="block space-y-1.5">
						<LabelWithHint label="Keep in reserve">{FLOOR_HELP}</LabelWithHint>
						<Input
							type="number"
							min="0"
							step="1"
							value={draft.floor}
							onChange={(event) => setDraft({ ...draft, floor: event.target.value })}
						/>
					</label>
				</div>

				<div className="grid gap-4 sm:grid-cols-2">
					<label className="block space-y-1.5">
						<LabelWithHint label="Discount">{DISCOUNT_HELP}</LabelWithHint>
						<Select
							value={draft.discountKind}
							onChange={(event) =>
								setDraft({ ...draft, discountKind: event.target.value as GiftDiscountKind })
							}
						>
							<option value="free">Free</option>
							<option value="percentage">Percentage off</option>
							<option value="fixed_amount">Amount off</option>
						</Select>
					</label>
					{draft.discountKind === "free" ? null : (
						<label className="block space-y-1.5">
							<span className="text-sm text-ink">
								{draft.discountKind === "percentage" ? "Percent off" : "Amount off"}
							</span>
							<Input
								type="number"
								min="0"
								max={draft.discountKind === "percentage" ? "100" : undefined}
								step={draft.discountKind === "percentage" ? "1" : "0.01"}
								value={draft.discountAmount}
								onChange={(event) => setDraft({ ...draft, discountAmount: event.target.value })}
							/>
						</label>
					)}
				</div>

				{node.sellingPlans.length === 0 ? (
					<p className="border-t border-line pt-4 text-xs text-ink-muted">
						This product is not sold on a subscription, so there is nothing to set here. Add a
						selling plan in Shopify and run a subscription sync to change that.
					</p>
				) : (
					<div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-2">
						<label className="block space-y-1.5">
							<LabelWithHint label="Subscription">{SUBSCRIPTION_HELP}</LabelWithHint>
							<Select
								value={draft.subscriptionMode}
								onChange={(event) =>
									setDraft({
										...draft,
										subscriptionMode: event.target.value as GiftSubscriptionMode,
									})
								}
							>
								<option value="none">Not available</option>
								<option value="optional">Optional</option>
								<option value="required">Required</option>
							</Select>
						</label>

						{draft.subscriptionMode === "none" ? null : (
							<label className="block space-y-1.5">
								<LabelWithHint label="Plan">{PLAN_HELP}</LabelWithHint>
								<Select
									value={draft.sellingPlanGid ?? ""}
									onChange={(event) =>
										setDraft({ ...draft, sellingPlanGid: event.target.value || null })
									}
								>
									<option value="">Any plan</option>
									<SellingPlanOptions plans={node.sellingPlans} />
								</Select>
							</label>
						)}
					</div>
				)}
			</div>
		</Dialog>
	);
};
